import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth0 } from '../../../auth/useAuth0';
import { useBusinessContext } from '../../../context/BusinessContext';
import { useNavigation } from '../../../context/NavigationContext';
import { MENU } from '../../util/Utils';
import { useTimeFormat, formatClock } from '../../../util/timeFormat';
import DateTimeField from '../../commons/datetime/DateTimeField';
import { useAlert } from '../../../context/alert/AlertContext';
import { useConfirm } from '../../../context/confirm/ConfirmContext';
import LoadingComponent from '../../commons/LoadingComponent';
import Pagination from '../../commons/pagination/Pagination';
import Drawer from '../../commons/drawer/Drawer';
import { Appointment } from '../../../model/appointment/Appointment';
import {
    AppointmentPaymentStatus,
    APPOINTMENT_PAYMENT_LABEL,
} from '../../../model/appointment/AppointmentPaymentStatus';
import {
    AppointmentStatus,
    APPOINTMENT_STATUS_OPTIONS,
} from '../../../model/appointment/AppointmentStatus';
import { AppointmentHistoryEvent } from '../../../model/appointment/AppointmentHistoryEvent';
import { AppointmentInput } from '../../../service/appointment/AppointmentInput';
import {
    checkInAppointment,
    callPatient,
    deleteAppointment,
    getAppointmentById,
    getAppointmentHistory,
    getAppointmentsByBusinessAndRange,
    saveAppointment,
} from '../../../service/appointment/appointment-service';
import {
    getTransactionsByAppointment,
} from '../../../service/payment/transaction-service';
import {
    Transaction,
    TRANSACTION_STATUS_LABEL,
} from '../../../model/payment/Transaction';
import { Service } from '../../../model/service/Service';
import { TeamMember } from '../../../model/teammember/TeamMember';
import { getServicesByBusinessId } from '../../../service/service/service-service';
import { getTeamMembersByBusinessId } from '../../../service/teammember/team-member-service';
import { getAllClientsByBusinessId } from '../../../service/client/client-service';
import { Client } from '../../../model/client/Client';
import { currencySymbol } from '../../../util/money';
import { formatPhone, isPhoneComplete, resolvePhoneCountry } from '../../../util/phone';
import { findBusinessSettingByBusinessId } from '../../../service/business/business-service';
import { getMyPermissionGrant, PermissionGrant } from '../../../service/permission/permission-service';
import './AppointmentsComponent.css';

/* --------- Date presets --------- */
type DatePreset =
    | 'UPCOMING'
    | 'TODAY' | 'YESTERDAY' | 'THIS_WEEK'
    | 'THIS_MONTH' | 'LAST_MONTH'
    | 'THIS_QUARTER' | 'LAST_QUARTER'
    | 'THIS_YEAR' | 'LAST_YEAR'
    | 'CUSTOM';

const DATE_PRESET_KEYS = [
    'UPCOMING', 'TODAY', 'YESTERDAY', 'THIS_WEEK',
    'THIS_MONTH', 'LAST_MONTH', 'THIS_QUARTER', 'LAST_QUARTER',
    'THIS_YEAR', 'LAST_YEAR', 'CUSTOM',
] as const;

const ymd = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const presetRange = (p: DatePreset): { start: string; end: string } => {
    const today = new Date();
    const y = today.getFullYear();
    const m = today.getMonth();
    const dayOfWeek = today.getDay();
    const mondayOffset = (dayOfWeek + 6) % 7;
    switch (p) {
        case 'UPCOMING': {
            const e = new Date(today); e.setDate(today.getDate() + 90);
            return { start: ymd(today), end: ymd(e) };
        }
        case 'TODAY':     return { start: ymd(today), end: ymd(today) };
        case 'YESTERDAY': {
            const y1 = new Date(today); y1.setDate(today.getDate() - 1);
            return { start: ymd(y1), end: ymd(y1) };
        }
        case 'THIS_WEEK': {
            const s = new Date(today); s.setDate(today.getDate() - mondayOffset);
            const e = new Date(s); e.setDate(s.getDate() + 6);
            return { start: ymd(s), end: ymd(e) };
        }
        case 'THIS_MONTH':   return { start: ymd(new Date(y, m, 1)),     end: ymd(new Date(y, m + 1, 0)) };
        case 'LAST_MONTH':   return { start: ymd(new Date(y, m - 1, 1)), end: ymd(new Date(y, m, 0)) };
        case 'THIS_QUARTER': {
            const q = Math.floor(m / 3) * 3;
            return { start: ymd(new Date(y, q, 1)), end: ymd(new Date(y, q + 3, 0)) };
        }
        case 'LAST_QUARTER': {
            const q = Math.floor(m / 3) * 3 - 3;
            const qy = q < 0 ? y - 1 : y;
            const real = ((q % 12) + 12) % 12;
            return { start: ymd(new Date(qy, real, 1)), end: ymd(new Date(qy, real + 3, 0)) };
        }
        case 'THIS_YEAR':    return { start: ymd(new Date(y, 0, 1)), end: ymd(new Date(y, 11, 31)) };
        case 'LAST_YEAR':    return { start: ymd(new Date(y - 1, 0, 1)), end: ymd(new Date(y - 1, 11, 31)) };
        default:             return { start: '', end: '' };
    }
};

const toLocalInput = (iso: string) => (iso?.length >= 16 ? iso.slice(0, 16) : iso ?? '');
const withSeconds = (s: string) => (s.length === 16 ? s + ':00' : s);

type DetailTab = 'details' | 'payment' | 'history';

/** Sortable columns of the appointments table. */
type SortKey = 'id' | 'when' | 'customer' | 'status' | 'payment' | 'discount' | 'totalDue' | 'paid';
type SortDir = 'asc' | 'desc';

/** Default direction when a column is first clicked. Text columns read best
 *  A→Z; dates/amounts read best newest/largest first. */
const DEFAULT_DIR: Record<SortKey, SortDir> = {
    id: 'desc', when: 'desc', customer: 'asc', status: 'asc',
    payment: 'asc', discount: 'desc', totalDue: 'desc', paid: 'desc',
};

/** Returns true when the appointment's startTime falls on today (local date). */
const isApptToday = (appt: Appointment): boolean => {
    const apptDate = appt.startTime.slice(0, 10); // "YYYY-MM-DD"
    const today = ymd(new Date());
    return apptDate === today;
};

/** Statuses that should never show waiting-room actions. */
const TERMINAL_STATUSES: AppointmentStatus[] = ['CANCELLED', 'DECLINED', 'NO_SHOW', 'COMPLETED'];

/** Minutes since the scheduled start (negative = still upcoming). */
const minutesPastStart = (iso?: string | null): number => {
    if (!iso) return -1;
    const t = new Date(iso).getTime();
    return isNaN(t) ? -1 : Math.floor((Date.now() - t) / 60_000);
};
/** No-show grace window: stop offering the "Chegou" check-in once a patient is
 *  more than this many minutes late (matches the waiting-room dropdown). */
const WAITING_LATE_LIMIT_MIN = 60;
/** Can reception still check this patient in? Today, not yet arrived, and not
 *  more than 1h past the scheduled time (after that it's treated as a no-show). */
const canCheckIn = (appt: Appointment): boolean =>
    !appt.arrivedAt && isApptToday(appt) && minutesPastStart(appt.startTime) <= WAITING_LATE_LIMIT_MIN;

const AppointmentsComponent: React.FC = () => {
    const { t } = useTranslation();
    const confirmDialog = useConfirm();
    const timeFmt = useTimeFormat();

    // --- Appointment-history localization (issue #54) ---
    // The audit log stores raw English event types + an "A → B" status payload.
    // Translate both to the UI language; fall back to the raw value if a key is missing.
    const histEventLabel = (eventType: string): string =>
        t(`appointments.drawer.history.event.${eventType}`, { defaultValue: eventType });
    const histPayloadLabel = (eventType: string, payload: string): string => {
        const sep = payload.includes('→') ? '→' : payload.includes('->') ? '->' : null;
        // Only status/payment-status payloads are "A → B" enums; discounts etc. are numeric → leave as-is.
        if (!sep || (eventType !== 'STATUS_CHANGED' && eventType !== 'PAYMENT_STATUS_CHANGED')) return payload;
        const map = eventType === 'PAYMENT_STATUS_CHANGED' ? 'paymentStatus' : 'status';
        return payload
            .split(sep)
            .map((s) => t(`appointments.${map}.${s.trim()}`, { defaultValue: s.trim() }))
            .join(' → ');
    };
    const { getAccessTokenSilently } = useAuth0();
    const { selectedBusiness } = useBusinessContext();
    const { setActiveMenu, setPendingAppointmentId, pendingAppointmentId, setPendingPaymentAppointmentId } = useNavigation();

    /** Jump to the Payments screen pre-filtered to this appointment's payments. */
    const openAppointmentPayments = (appointmentId: string) => {
        setPendingPaymentAppointmentId(appointmentId);
        setActiveMenu(MENU.PAYMENTS);
    };
    const { setAlert } = useAlert();

    const goRecordPayment = (id: string) => {
        setPendingAppointmentId(id);
        setActiveMenu(MENU.CALENDAR);
    };

    const businessId = selectedBusiness?.id ? String(selectedBusiness.id) : '';
    const currency = currencySymbol(selectedBusiness?.currency);

    // Waiting room feature flag — fetched from business settings.
    const [enableWaitingRoom, setEnableWaitingRoom] = useState(false);
    const [waitingRoomLoading, setWaitingRoomLoading] = useState(false);

    // Filters
    const [datePreset, setDatePreset] = useState<DatePreset>('UPCOMING');
    const initial = presetRange('UPCOMING');
    const [startDate, setStartDate] = useState(initial.start);
    const [endDate, setEndDate] = useState(initial.end);
    const [statusFilter, setStatusFilter] = useState<AppointmentStatus | ''>('');
    const [paymentFilter, setPaymentFilter] = useState<AppointmentPaymentStatus | ''>('');
    const [teamMemberFilter, setTeamMemberFilter] = useState<string>('');
    // Raw text in the ID box (updates on every keystroke) vs the APPLIED id used
    // for the lookup. Decoupling them lets the user type a full id (e.g. "168")
    // without each digit triggering a refetch and collapsing the toolbar — which
    // remounted the surrounding subtree and stole focus mid-typing.
    const [idFilter, setIdFilter] = useState<string>('');
    const [appliedId, setAppliedId] = useState<string>('');
    /** Monotonic token so an older in-flight reload can't overwrite a newer one. */
    const reloadSeqRef = useRef(0);
    const [search, setSearch] = useState('');
    /** appointmentId → list of transactions (for Total Due / Paid columns + detail drawer). */
    const [txnsByAppt, setTxnsByAppt] = useState<Record<string, Transaction[]>>({});
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    /** Column sort. The default period is UPCOMING (next 90 days incl. today),
     *  so we open ascending by "when" — the soonest appointment first. Past
     *  periods flip to descending (most recent first) when picked. */
    const [sort, setSort] = useState<{ key: SortKey; dir: SortDir }>({ key: 'when', dir: 'asc' });

    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [services, setServices] = useState<Service[]>([]);
    const [members, setMembers] = useState<TeamMember[]>([]);
    const [clients, setClients] = useState<Client[]>([]);
    const [loading, setLoading] = useState(true);
    // True once the first load resolved. After that, refetches (e.g. typing an
    // id) keep the table mounted instead of swapping to a full-screen loader —
    // that swap unmounted the toolbar and stole focus from the id box (#44).
    const [didInitialLoad, setDidInitialLoad] = useState(false);

    // Detail-drawer state
    const [draft, setDraft] = useState<Appointment | null>(null);
    const [activeTab, setActiveTab] = useState<DetailTab>('details');
    const [drawerTxns, setDrawerTxns] = useState<Transaction[]>([]);
    const [drawerHistory, setDrawerHistory] = useState<AppointmentHistoryEvent[]>([]);
    const [saving, setSaving] = useState(false);

    /** The caller's CALENDAR grant — same permission the Calendar screen uses.
     *  A view-only member (CALENDAR = VIEW) can open an appointment but must not
     *  see Save/Delete; the calendar-service re-checks edit on every write. */
    const [calGrant, setCalGrant] = useState<PermissionGrant | null>(null);
    const canEditCalendar = !!calGrant?.canEdit;

    /** A cancelled/declined/completed/no-show appointment is final — its details
     *  are read-only. Based on the PERSISTED status (from the list), not the live
     *  dropdown, so transitioning a confirmed appt INTO a terminal status and
     *  saving still works; only an already-terminal appt locks. */
    const editingAppt = draft?.id ? appointments.find((a) => String(a.id) === String(draft.id)) : undefined;
    const lockedAppt = !!editingAppt && TERMINAL_STATUSES.includes(editingAppt.status);

    const startIso = startDate ? `${startDate}T00:00:00` : null;
    const endIso = useMemo(() => {
        if (!endDate) return null;
        const [yy, mm, dd] = endDate.split('-').map(Number);
        const next = new Date(yy, mm - 1, dd);
        next.setDate(next.getDate() + 1);
        return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}T00:00:00`;
    }, [endDate]);

    // Catalog lookups for the edit form.
    useEffect(() => {
        (async () => {
            try {
                const token = await getAccessTokenSilently();
                // Business-scoped catalog lookups.
                const [svcs, tms, cls, calG] = await Promise.all([
                    businessId ? getServicesByBusinessId(businessId, token) : Promise.resolve([]),
                    businessId ? getTeamMembersByBusinessId(businessId, token) : Promise.resolve([]),
                    businessId ? getAllClientsByBusinessId(businessId, token) : Promise.resolve([]),
                    businessId ? getMyPermissionGrant(businessId, 'CALENDAR', token).catch(() => null) : Promise.resolve(null),
                ]);
                setServices(svcs ?? []);
                setMembers(tms ?? []);
                setClients(cls ?? []);
                setCalGrant(calG);
            } catch { /* non-critical */ }
        })();
    }, [businessId, getAccessTokenSilently]);

    // Fetch the waiting-room feature flag from business settings.
    useEffect(() => {
        if (!selectedBusiness?.id) return;
        (async () => {
            setWaitingRoomLoading(true);
            try {
                const token = await getAccessTokenSilently();
                const settings = await findBusinessSettingByBusinessId(selectedBusiness.id, token);
                setEnableWaitingRoom(!!settings?.enableWaitingRoom);
            } catch { /* non-critical — feature stays off */ }
            finally { setWaitingRoomLoading(false); }
        })();
    }, [selectedBusiness?.id, getAccessTokenSilently]);

    // Debounce the typed id into appliedId so the lookup waits until the user
    // stops typing (400ms) instead of firing per keystroke.
    useEffect(() => {
        const h = setTimeout(() => setAppliedId(idFilter.trim()), 400);
        return () => clearTimeout(h);
    }, [idFilter]);

    const reload = useCallback(async () => {
        if (!businessId) return;
        const idQ = appliedId;
        // When an ID is typed, fetch that one appointment directly and
        // ignore the period filter — IDs are global, so requiring the
        // user to also pick the right date window was pointless friction
        // (e.g. clicking a #9 in Payments couldn't find an old appt).
        if (!idQ && (!startIso || !endIso)) return;
        // Guard against out-of-order responses: a deep-link from Payments kicks
        // off the date-range load and the by-id load back-to-back. If the slower
        // range load resolves last it would clobber the id result with a list
        // that doesn't contain the (out-of-range) appointment, leaving an empty
        // table. Only the most recent reload is allowed to write state.
        const seq = ++reloadSeqRef.current;
        try {
            setLoading(true);
            const token = await getAccessTokenSilently();
            let data: Appointment[];
            if (idQ) {
                const one = await getAppointmentById(idQ, token).catch(() => null);
                data = one ? [one] : [];
            } else {
                data = await getAppointmentsByBusinessAndRange(
                    businessId, startIso!, endIso!, token
                );
            }
            if (seq !== reloadSeqRef.current) return; // superseded by a newer reload
            setAppointments(data);
            // Pull transactions for each appointment in parallel so the table
            // can show Total Due and Paid columns. Failures per-appointment
            // collapse to an empty list — the row just renders 0 paid.
            try {
                const entries = await Promise.all(
                    data.map((a) =>
                        getTransactionsByAppointment(a.id, token)
                            .then((txs) => [a.id, txs] as const)
                            .catch(() => [a.id, [] as Transaction[]] as const)
                    )
                );
                if (seq !== reloadSeqRef.current) return; // superseded
                const map: Record<string, Transaction[]> = {};
                for (const [id, txs] of entries) map[id] = txs;
                setTxnsByAppt(map);
            } catch {
                if (seq === reloadSeqRef.current) setTxnsByAppt({});
            }
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('appointments.alerts.failLoad'));
        } finally {
            // Only the latest reload flips the loading/initial flags so a stale
            // response can't toggle the spinner back on under the current view.
            if (seq === reloadSeqRef.current) {
                setLoading(false);
                setDidInitialLoad(true);
            }
        }
    }, [businessId, startIso, endIso, appliedId, getAccessTokenSilently, setAlert, t]);

    useEffect(() => { reload(); }, [reload]);

    /** Auto-open the detail drawer when navigated here via setPendingAppointmentId
     *  (Payments / Clients screens → Appointments). Bypass the period filter by
     *  pushing the id into idFilter — reload fetches the single appt directly. */
    useEffect(() => {
        if (!pendingAppointmentId) return;
        setIdFilter(String(pendingAppointmentId));
        setAppliedId(String(pendingAppointmentId)); // apply immediately — no debounce wait for deep-links
        setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingAppointmentId]);

    /** Once the pending appointment has been fetched, open its drawer. */
    useEffect(() => {
        if (!pendingAppointmentId || appointments.length === 0) return;
        const appt = appointments.find((a) => String(a.id) === String(pendingAppointmentId));
        if (appt) openDetail(appt);
        setPendingAppointmentId(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingAppointmentId, appointments]);

    /** True when an ID lookup is active — collapses the period/status/payment/
     *  team-member/customer filters since the ID query already returned the
     *  single appointment the user is looking at. Mixing the two is what
     *  silently emptied the list when navigating from Payments. */
    const idLookupActive = appliedId.length > 0;

    /** Derived money columns for one appointment — kept in one place so the
     *  table row and the sort comparator agree. Mirrors the row render below. */
    const computeAmounts = useCallback((a: Appointment) => {
        const svc = services.find((s) => s.id === a.serviceId);
        const cli = clients.find((c) => String(c.id) === String(a.clientId));
        const base = a.price ?? svc?.price ?? 0;
        // Clamp to [0,100] like the calendar checkout — a stray/negative discount
        // must never turn into a surcharge.
        const pct = Math.max(0, Math.min(100, Number(cli?.discountPercent) || 0));
        const cdisc = +(base * pct / 100).toFixed(2);
        const edisc = a.extraDiscount ?? 0;
        const discount = +(cdisc + edisc).toFixed(2);
        const totalDue = +(base - cdisc - edisc).toFixed(2);
        const paid = (txnsByAppt[a.id] ?? []).reduce((s, t) => {
            if (t.status === 'SUCCEEDED' || t.status === 'PARTIALLY_REFUNDED') {
                return s + (t.amount - (t.refundedAmount || 0));
            }
            return s;
        }, 0);
        return { discount, totalDue, paid };
    }, [services, clients, txnsByAppt]);

    /** Comparator for the active sort column. Returns a<b ordering (asc); the
     *  caller flips it for 'desc'. Strings compare case-insensitively. */
    const compareBy = useCallback((a: Appointment, b: Appointment, key: SortKey): number => {
        switch (key) {
            case 'id':       return Number(a.id) - Number(b.id);
            case 'when':     return a.startTime < b.startTime ? -1 : a.startTime > b.startTime ? 1 : 0;
            case 'customer': return (a.customerName ?? '').localeCompare(b.customerName ?? '', undefined, { sensitivity: 'base' });
            case 'status':   return (a.status ?? '').localeCompare(b.status ?? '');
            case 'payment':  return (a.paymentStatus ?? '').localeCompare(b.paymentStatus ?? '');
            case 'discount': return computeAmounts(a).discount - computeAmounts(b).discount;
            case 'totalDue': return computeAmounts(a).totalDue - computeAmounts(b).totalDue;
            case 'paid':     return computeAmounts(a).paid - computeAmounts(b).paid;
            default:         return 0;
        }
    }, [computeAmounts]);

    /** Click a column header: toggle direction if it's the active column,
     *  otherwise switch to it with its natural default direction. */
    const toggleSort = (key: SortKey) => {
        setSort((prev) => prev.key === key
            ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
            : { key, dir: DEFAULT_DIR[key] });
        setPage(1);
    };

    const sortRows = useCallback((rows: Appointment[]): Appointment[] => {
        const dir = sort.dir === 'asc' ? 1 : -1;
        // Stable tie-break on startTime desc keeps same-key rows in a sensible order.
        return [...rows].sort((a, b) => {
            const primary = compareBy(a, b, sort.key);
            if (primary !== 0) return primary * dir;
            return a.startTime < b.startTime ? 1 : a.startTime > b.startTime ? -1 : 0;
        });
    }, [sort, compareBy]);

    // Team members offered in the filter dropdown: only active ones (a
    // deactivated member shouldn't be a filter option), plus the currently
    // selected one if it happens to be deactivated so the selection isn't lost.
    const filterMembers = useMemo(
        () => members.filter((m) => m.active !== false || String(m.id) === teamMemberFilter),
        [members, teamMemberFilter],
    );

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const idQ = appliedId;
        // ID lookup is exclusive — appointment.id is global, so any other
        // filter applied on top of it can only hide a valid match.
        if (idQ) {
            return sortRows(appointments.filter((a) => String(a.id).includes(idQ)));
        }
        return sortRows(appointments
            .filter((a) => !statusFilter   || a.status === statusFilter)
            .filter((a) => !paymentFilter  || a.paymentStatus === paymentFilter)
            .filter((a) => !teamMemberFilter || String(a.teamMemberId) === teamMemberFilter)
            .filter((a) => !q || (a.customerName ?? '').toLowerCase().includes(q)));
    }, [appointments, statusFilter, paymentFilter, teamMemberFilter, appliedId, search, sortRows]);

    const pageItems = useMemo(() => {
        const from = (page - 1) * perPage;
        return filtered.slice(from, from + perPage);
    }, [filtered, page, perPage]);

    /**
     * Refresh the appointment's denormalized customer snapshot from the linked
     * client. Name/phone/email are copied onto the appointment at booking, so a
     * later client rename leaves the list showing the old value. On open we
     * re-read the current client and, if it drifted, update the drawer + the row
     * and persist a full re-save in the background — no manual Save. Every other
     * field (incl. price, externalLocation) is resent unchanged so the mapper's
     * null-overwrite never wipes anything; status is unchanged → no notification.
     */
    const syncLinkedClientSnapshot = async (a: Appointment) => {
        if (!a.id || !a.clientId || !businessId) return;
        const client = clients.find((c) => String(c.id) === String(a.clientId));
        if (!client) return;
        const freshName = `${client.firstName}${client.lastName ? ' ' + client.lastName : ''}`;
        const freshEmail = client.email ?? a.customerEmail ?? null;
        const freshPhone = client.phone ?? a.customerPhone ?? null;
        const drifted =
            freshName !== (a.customerName ?? '') ||
            (freshEmail ?? '') !== (a.customerEmail ?? '') ||
            (freshPhone ?? '') !== (a.customerPhone ?? '');
        if (!drifted) return;

        setDraft((prev) =>
            prev && prev.id === a.id
                ? { ...prev, customerName: freshName, customerEmail: freshEmail, customerPhone: freshPhone }
                : prev
        );
        try {
            const token = await getAccessTokenSilently();
            const saved = await saveAppointment(
                {
                    id: a.id,
                    businessId,
                    teamMemberId: a.teamMemberId,
                    serviceId: a.serviceId,
                    clientId: a.clientId,
                    customerName: freshName,
                    customerPhone: freshPhone,
                    customerEmail: freshEmail,
                    startTime: withSeconds(a.startTime),
                    endTime: withSeconds(a.endTime),
                    status: a.status,
                    extraDiscount: a.extraDiscount ?? 0,
                    price: a.price ?? null,
                    notes: a.notes ?? null,
                    externalLocation: a.externalLocation ?? null,
                    cancellationReason:
                        a.status === 'CANCELLED' || a.status === 'DECLINED'
                            ? a.cancellationReason ?? null
                            : null,
                },
                token
            );
            setAppointments((prev) =>
                prev.map((x) =>
                    x.id === a.id
                        ? saved ?? { ...x, customerName: freshName, customerEmail: freshEmail, customerPhone: freshPhone }
                        : x
                )
            );
        } catch {
            /* best-effort: the snapshot is retried next time the drawer opens */
        }
    };

    /** Open detail Drawer + lazy-fetch transactions + history. */
    const openDetail = async (a: Appointment) => {
        // Render the linked client's CURRENT name/contact, not the (possibly
        // stale) snapshot on the appointment; the sync below also persists it.
        const linked = a.clientId
            ? clients.find((c) => String(c.id) === String(a.clientId))
            : null;
        setDraft(
            linked
                ? {
                      ...a,
                      customerName: `${linked.firstName}${linked.lastName ? ' ' + linked.lastName : ''}`,
                      customerPhone: linked.phone ?? a.customerPhone ?? null,
                      customerEmail: linked.email ?? a.customerEmail ?? null,
                  }
                : { ...a }
        );
        setActiveTab('details');
        // Re-sync the customer snapshot from the linked client (see helper above).
        void syncLinkedClientSnapshot(a);
        setDrawerTxns([]);
        setDrawerHistory([]);
        try {
            const token = await getAccessTokenSilently();
            const [txns, hist] = await Promise.all([
                getTransactionsByAppointment(a.id, token),
                getAppointmentHistory(a.id, token),
            ]);
            setDrawerTxns(txns);
            setDrawerHistory(hist);
        } catch { /* tolerable */ }
    };

    const closeDrawer = () => {
        setDraft(null);
        setDrawerTxns([]);
        setDrawerHistory([]);
    };

    const handleSave = async () => {
        if (!draft) return;
        if (!canEditCalendar) return; // view-only: backend rejects writes too
        if (lockedAppt) return; // cancelled/completed appts are read-only
        if (!draft.customerName?.trim()) {
            setAlert('danger', t('appointments.alerts.customerRequired'));
            return;
        }
        if (!draft.startTime || !draft.endTime) {
            setAlert('danger', t('appointments.alerts.timesRequired'));
            return;
        }
        const phoneCountry = resolvePhoneCountry(undefined, selectedBusiness?.currency);
        if (!isPhoneComplete(draft.customerPhone ?? '', phoneCountry)) {
            setAlert('danger', t('common.invalidPhone'));
            return;
        }
        try {
            setSaving(true);
            const token = await getAccessTokenSilently();
            const input: AppointmentInput = {
                id: draft.id,
                businessId,
                teamMemberId: draft.teamMemberId,
                serviceId: draft.serviceId,
                clientId: draft.clientId ?? null,
                customerName: draft.customerName.trim(),
                customerPhone: draft.customerPhone || null,
                customerEmail: draft.customerEmail || null,
                startTime: withSeconds(draft.startTime),
                endTime: withSeconds(draft.endTime),
                status: draft.status,
                extraDiscount: draft.extraDiscount ?? 0,
                // Preserve the per-appointment price of VARIABLE services.
                price: draft.price ?? null,
                notes: draft.notes || null,
            };
            await saveAppointment(input, token);
            setAlert('success', t('appointments.alerts.saved'));
            // Refresh row in the table + reload history.
            const fresh = await getAppointmentById(draft.id, token);
            if (fresh) {
                setAppointments((prev) =>
                    prev.map((a) => (a.id === draft.id ? fresh : a))
                );
                setDraft(fresh);
                const hist = await getAppointmentHistory(draft.id, token);
                setDrawerHistory(hist);
            }
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('appointments.alerts.failSave'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!draft) return;
        if (!canEditCalendar) return; // view-only: backend rejects deletes too
        if (!(await confirmDialog({ title: t('common.confirmTitle'), message: t('appointments.alerts.confirmDelete', { id: draft.id, name: draft.customerName }), danger: true, confirmLabel: t('common.delete') }))) {
            return;
        }
        try {
            setSaving(true);
            const token = await getAccessTokenSilently();
            await deleteAppointment(draft.id, token);
            setAlert('success', t('appointments.alerts.deleted'));
            setAppointments((prev) => prev.filter((a) => a.id !== draft.id));
            closeDrawer();
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('appointments.alerts.failDelete'));
        } finally {
            setSaving(false);
        }
    };

    /** Update a single appointment in both the table list and the open drawer. */
    const patchAppointment = (updated: Appointment) => {
        setAppointments((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
        setDraft((prev) => (prev && prev.id === updated.id ? updated : prev));
    };

    const handleCheckIn = async (appt: Appointment) => {
        try {
            const token = await getAccessTokenSilently();
            const updated = await checkInAppointment(token, appt.id);
            patchAppointment(updated);
            // Tell the header's waiting-room chip to refresh immediately. actedId
            // lets the header suppress the sound/flash in this same tab.
            window.dispatchEvent(new CustomEvent('gs:waiting-room-changed', { detail: { actedId: appt.id } }));
            setAlert('success', t('appointments.alerts.checkedIn', { name: appt.customerName }));
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('appointments.alerts.failCheckIn'));
        }
    };

    const handleCallPatient = async (appt: Appointment) => {
        try {
            const token = await getAccessTokenSilently();
            const updated = await callPatient(token, appt.id);
            patchAppointment(updated);
            window.dispatchEvent(new CustomEvent('gs:waiting-room-changed', { detail: { actedId: appt.id } }));
            setAlert('success', t('appointments.alerts.called', { name: appt.customerName }));
        } catch (e) {
            setAlert('danger', e instanceof Error ? e.message : t('appointments.alerts.failCall'));
        }
    };

    if (loading && !didInitialLoad) return <LoadingComponent />;

    /** A clickable, sortable column header with an active-direction caret. */
    const sortableTh = (key: SortKey, label: string, numeric = false) => {
        const active = sort.key === key;
        return (
            <th
                className={`appointments-th-sort${numeric ? ' appointments-num' : ''}${active ? ' appointments-th-sort--active' : ''}`}
                data-cy={`appts-sort-${key}`}
                onClick={() => toggleSort(key)}
                role="button"
                aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                title={`Sort by ${label}`}
            >
                {label}
                <span className="appointments-th-arrow">{active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
            </th>
        );
    };

    return (
        <div className="appointments-page" data-cy="appts-page">
            <div className="list-card">
                {/* Toolbar */}
                <div className="appointments-toolbar">
                    <div className="appointments-toolbar__filters">
                        {!idLookupActive && (<>
                        <label>
                            {t('appointments.filters.period')}
                            <select
                                value={datePreset}
                                onChange={(e) => {
                                    const next = e.target.value as DatePreset;
                                    setDatePreset(next);
                                    if (next !== 'CUSTOM') {
                                        const r = presetRange(next);
                                        setStartDate(r.start);
                                        setEndDate(r.end);
                                    }
                                    // Forward-looking period reads best soonest-first; past
                                    // periods most-recent-first. Realign the "when" sort.
                                    setSort({ key: 'when', dir: next === 'UPCOMING' ? 'asc' : 'desc' });
                                    setPage(1);
                                }}
                            >
                                {DATE_PRESET_KEYS.map((k) => (
                                    <option key={k} value={k}>{t(`appointments.presets.${k}`)}</option>
                                ))}
                            </select>
                        </label>
                        {datePreset === 'CUSTOM' && (
                            <>
                                <label>
                                    {t('appointments.filters.from')}
                                    <input type="date" value={startDate}
                                        onChange={(e) => { setStartDate(e.target.value); setPage(1); }} />
                                </label>
                                <label>
                                    {t('appointments.filters.to')}
                                    <input type="date" value={endDate}
                                        onChange={(e) => { setEndDate(e.target.value); setPage(1); }} />
                                </label>
                            </>
                        )}
                        <label>
                            {t('appointments.filters.status')}
                            <select
                                data-cy="appts-filter-status"
                                value={statusFilter}
                                onChange={(e) => { setStatusFilter(e.target.value as AppointmentStatus | ''); setPage(1); }}
                            >
                                <option value="">{t('common.all')}</option>
                                {APPOINTMENT_STATUS_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>{t(`appointments.status.${o.value}`)}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            {t('appointments.filters.payment')}
                            <select
                                data-cy="appts-filter-payment"
                                value={paymentFilter}
                                onChange={(e) => { setPaymentFilter(e.target.value as AppointmentPaymentStatus | ''); setPage(1); }}
                            >
                                <option value="">{t('common.all')}</option>
                                {(Object.keys(APPOINTMENT_PAYMENT_LABEL) as AppointmentPaymentStatus[]).map((k) => (
                                    <option key={k} value={k}>{t(`appointments.paymentStatus.${k}`)}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            {t('appointments.filters.teamMember')}
                            <select
                                data-cy="appts-filter-team-member"
                                value={teamMemberFilter}
                                onChange={(e) => { setTeamMemberFilter(e.target.value); setPage(1); }}
                            >
                                <option value="">{t('common.all')}</option>
                                {filterMembers.map((tm) => (
                                    <option key={tm.id} value={String(tm.id)}>
                                        {tm.firstName} {tm.lastName ?? ''}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label>
                            {t('appointments.filters.customer')}
                            <input
                                type="text"
                                data-cy="appts-search-input"
                                placeholder={t('appointments.filters.customerPlaceholder')}
                                value={search}
                                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            />
                        </label>
                        </>)}
                        {idLookupActive && (
                            <div className="appointments-toolbar__hint">
                                {t('appointments.filters.idHint', { id: appliedId })}
                            </div>
                        )}
                    </div>
                    {/* ID lives on the right so the user understands it is
                        an independent lookup — it bypasses every other
                        filter (an id is global). */}
                    <div className="appointments-toolbar__right">
                        <label>
                            {t('appointments.filters.id')}
                            <input
                                type="text"
                                placeholder={t('appointments.filters.idPlaceholder')}
                                value={idFilter}
                                onChange={(e) => { setIdFilter(e.target.value); setPage(1); }}
                                style={{ maxWidth: '90px' }}
                                title={t('appointments.filters.idTitle')}
                            />
                        </label>
                        {idLookupActive && (
                            <button
                                type="button"
                                className="btn btn-link btn-sm appointments-toolbar__clear"
                                onClick={() => { setIdFilter(''); setAppliedId(''); setPage(1); }}
                                title={t('appointments.filters.idTitle')}
                            >
                                {t('appointments.filters.clearId')}
                            </button>
                        )}
                    </div>
                </div>

                {/* Table */}
                <div className="appointments-table-wrap">
                    <table className="appointments-table" data-cy="appts-table">
                        <thead>
                            <tr>
                                {sortableTh('id', t('appointments.table.appointment'))}
                                {sortableTh('when', t('appointments.table.when'))}
                                {sortableTh('customer', t('appointments.table.customer'))}
                                {sortableTh('status', t('appointments.table.status'))}
                                {sortableTh('payment', t('appointments.table.payment'))}
                                {sortableTh('discount', t('appointments.table.discount'))}
                                {sortableTh('totalDue', t('appointments.table.totalDue'), true)}
                                {sortableTh('paid', t('appointments.table.paid'), true)}
                            </tr>
                        </thead>
                        <tbody>
                            {pageItems.length === 0 && (
                                <tr><td colSpan={8} className="empty-hint">{t('appointments.table.noMatch')}</td></tr>
                            )}
                            {pageItems.map((a) => {
                                const meta = APPOINTMENT_PAYMENT_LABEL[a.paymentStatus];
                                const dt = new Date(a.startTime);
                                const sameYear = dt.getFullYear() === new Date().getFullYear();
                                // Total Due (after both discounts) and Paid (succeeded) —
                                // single source of truth shared with the sort comparator.
                                const { discount, totalDue, paid } = computeAmounts(a);
                                return (
                                    <tr key={a.id} onClick={() => openDetail(a)} className="appointments-row" data-cy={`appts-row-${a.id}`}>
                                        <td className="appointments-row__id">{a.id}</td>
                                        <td>
                                            <div>{dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: sameYear ? undefined : 'numeric' })}</div>
                                            <div className="appointments-row__sub">{formatClock(dt, timeFmt)}</div>
                                        </td>
                                        <td>{a.customerName || '—'}</td>
                                        <td>
                                            <span className={`appointments-status appointments-status--${a.status.toLowerCase()}`} data-cy="appts-status">{t(`appointments.status.${a.status}`, { defaultValue: a.status })}</span>
                                            {enableWaitingRoom && !TERMINAL_STATUSES.includes(a.status) && (canCheckIn(a) || !!a.arrivedAt) && (
                                                <span className="appts-wr-inline" onClick={(e) => e.stopPropagation()}>
                                                    {!a.arrivedAt ? (
                                                        <button
                                                            type="button"
                                                            className="btn appt-wr-btn appt-wr-btn--checkin"
                                                            data-cy={`appts-checkin-${a.id}`}
                                                            onClick={(e) => { e.stopPropagation(); handleCheckIn(a); }}
                                                        >
                                                            {t('waitingRoom.checkIn')}
                                                        </button>
                                                    ) : !a.calledAt ? (
                                                        <>
                                                            <span className="appt-wr-badge appt-wr-badge--waiting">{t('appointments.waitingRoom.inRoomBadge')}</span>
                                                            <button
                                                                type="button"
                                                                className="btn appt-wr-btn appt-wr-btn--call"
                                                                data-cy={`appts-call-${a.id}`}
                                                                onClick={(e) => { e.stopPropagation(); handleCallPatient(a); }}
                                                            >
                                                                {t('waitingRoom.call')}
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <span className="appt-wr-badge appt-wr-badge--called">{t('appointments.waitingRoom.calledBadge')}</span>
                                                    )}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <span className={`appointments-pay appointments-pay--${meta.tone}`}>{t(`appointments.paymentStatus.${a.paymentStatus}`, { defaultValue: meta.label })}</span>
                                            {a.paymentStatus !== 'NOT_PAID' && (
                                                <button
                                                    type="button"
                                                    className="appointments-pay-link"
                                                    data-cy={`appts-pay-link-${a.id}`}
                                                    onClick={(e) => { e.stopPropagation(); openAppointmentPayments(a.id); }}
                                                    title={t('appointments.viewPayments')}
                                                >
                                                    {t('appointments.viewPayments')}
                                                </button>
                                            )}
                                        </td>
                                        <td>{discount > 0 ? `−${currency}${discount.toFixed(2)}` : '—'}</td>
                                        <td className="appointments-num">{currency}{totalDue.toFixed(2)}</td>
                                        <td className="appointments-num">{currency}{paid.toFixed(2)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    page={page}
                    perPage={perPage}
                    totalItems={filtered.length}
                    onPageChange={setPage}
                    onPerPageChange={(n) => { setPerPage(n); setPage(1); }}
                />
            </div>

            <Drawer
                open={!!draft}
                onClose={closeDrawer}
                title={draft ? t('appointments.drawer.title', { id: draft.id }) : ''}
                size="md"
                footer={
                    draft ? (
                        <>
                            {activeTab === 'details' && canEditCalendar && !lockedAppt && (
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    data-cy="appts-drawer-save-btn"
                                    onClick={handleSave}
                                    disabled={saving}
                                >
                                    {saving ? t('common.working') : t('common.save')}
                                </button>
                            )}
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={closeDrawer}
                                disabled={saving}
                            >
                                {t('common.close')}
                            </button>
                            {canEditCalendar && (
                                <button
                                    type="button"
                                    className="btn btn-outline-danger ms-auto"
                                    data-cy="appts-drawer-delete-btn"
                                    onClick={handleDelete}
                                    disabled={saving}
                                >
                                    {t('common.delete')}
                                </button>
                            )}
                        </>
                    ) : null
                }
            >
                {draft && (
                    <div data-cy="appts-drawer">
                        <div className="gs-tab-strip">
                            {(['details', 'payment', 'history'] as DetailTab[]).map((tab) => (
                                <button
                                    key={tab}
                                    type="button"
                                    data-cy={`appts-drawer-tab-${tab}`}
                                    className={`gs-tab ${activeTab === tab ? 'gs-tab--active' : ''}`}
                                    onClick={() => setActiveTab(tab)}
                                >
                                    {t(`appointments.drawer.tabs.${tab}`)}
                                    {tab === 'payment' && (() => {
                                        const meta = APPOINTMENT_PAYMENT_LABEL[draft.paymentStatus];
                                        return (
                                            <span className={`gs-tab-badge gs-tab-badge--${meta.tone}`}>
                                                {t(`appointments.paymentStatus.${draft.paymentStatus}`, { defaultValue: meta.label })}
                                            </span>
                                        );
                                    })()}
                                </button>
                            ))}
                        </div>

                        {/* DETAILS — editable form, structured like the Calendar drawer:
                            one field per row by default, side-by-side only for tight pairs. */}
                        {activeTab === 'details' && (() => {
                            const isLinked = !!draft.clientId;
                            const showWR = enableWaitingRoom
                                && !TERMINAL_STATUSES.includes(draft.status)
                                && (canCheckIn(draft) || !!draft.arrivedAt);
                            return (
                            <div className="appt-edit-form">
                                {lockedAppt && (
                                    <div className="appt-edit-form__locked" data-cy="appts-locked-banner">
                                        {t('appointments.drawer.lockedHint', {
                                            status: t(`appointments.status.${draft.status}`),
                                        })}
                                    </div>
                                )}
                                {showWR && (
                                    <div className="appt-wr-section">
                                        <span className="appt-wr-section__label">{t('appointments.waitingRoom.label')}</span>
                                        {!draft.arrivedAt && (
                                            <button
                                                type="button"
                                                className="btn appt-wr-btn appt-wr-btn--checkin"
                                                onClick={() => handleCheckIn(draft)}
                                                disabled={waitingRoomLoading}
                                            >
                                                {t('waitingRoom.checkIn')}
                                            </button>
                                        )}
                                        {draft.arrivedAt && !draft.calledAt && (
                                            <>
                                                <span className="appt-wr-badge appt-wr-badge--waiting">
                                                    {t('appointments.waitingRoom.inRoom')}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="btn appt-wr-btn appt-wr-btn--call"
                                                    onClick={() => handleCallPatient(draft)}
                                                    disabled={waitingRoomLoading}
                                                >
                                                    {t('waitingRoom.call')}
                                                </button>
                                            </>
                                        )}
                                        {draft.calledAt && (
                                            <span className="appt-wr-badge appt-wr-badge--called">
                                                {t('appointments.waitingRoom.called')}
                                            </span>
                                        )}
                                    </div>
                                )}
                                <div className="appt-edit-form__group">
                                    <label className="appt-edit-form__label">{t('appointments.drawer.form.status')}</label>
                                    <select
                                        data-cy="appts-edit-status-select"
                                        value={draft.status}
                                        disabled={lockedAppt}
                                        onChange={(e) => setDraft({ ...draft, status: e.target.value as AppointmentStatus })}
                                    >
                                        {APPOINTMENT_STATUS_OPTIONS.map((o) => (
                                            <option key={o.value} value={o.value}>{t(`appointments.status.${o.value}`)}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="appt-edit-form__group">
                                    <label className="appt-edit-form__label">{t('appointments.drawer.form.service')}</label>
                                    <select
                                        value={draft.serviceId}
                                        disabled={lockedAppt}
                                        onChange={(e) => setDraft({ ...draft, serviceId: e.target.value })}
                                    >
                                        {services.map((s) => (
                                            <option key={s.id} value={s.id}>{s.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="appt-edit-form__group">
                                    <label className="appt-edit-form__label">{t('appointments.drawer.form.teamMember')}</label>
                                    <select
                                        value={draft.teamMemberId}
                                        disabled={lockedAppt}
                                        onChange={(e) => setDraft({ ...draft, teamMemberId: e.target.value })}
                                    >
                                        {members.map((tm) => (
                                            <option key={tm.id} value={tm.id}>
                                                {tm.firstName} {tm.lastName ?? ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="appt-edit-form__pair">
                                    <div className="appt-edit-form__group">
                                        <label className="appt-edit-form__label">{t('appointments.drawer.form.start')}</label>
                                        <DateTimeField
                                            value={toLocalInput(draft.startTime)}
                                            disabled={lockedAppt}
                                            onChange={(v) => setDraft({ ...draft, startTime: v })}
                                        />
                                    </div>
                                    <div className="appt-edit-form__group">
                                        <label className="appt-edit-form__label">{t('appointments.drawer.form.end')}</label>
                                        <DateTimeField
                                            value={toLocalInput(draft.endTime)}
                                            disabled={lockedAppt}
                                            onChange={(v) => setDraft({ ...draft, endTime: v })}
                                        />
                                    </div>
                                </div>

                                <div className="appt-edit-form__group">
                                    <label className="appt-edit-form__label">
                                        {t('appointments.drawer.form.customer')}
                                        {isLinked && (
                                            <span className="appt-edit-form__chip">{t('appointments.drawer.form.linkedClient')}</span>
                                        )}
                                    </label>
                                    <input
                                        type="text"
                                        value={draft.customerName}
                                        onChange={(e) => setDraft({ ...draft, customerName: e.target.value })}
                                        readOnly={isLinked}
                                        disabled={lockedAppt}
                                        title={isLinked ? t('appointments.drawer.form.linkedClientHint') : undefined}
                                    />
                                </div>

                                <div className="appt-edit-form__pair">
                                    <div className="appt-edit-form__group">
                                        <label className="appt-edit-form__label">{t('appointments.drawer.form.phone')}</label>
                                        <input
                                            type="text"
                                            value={draft.customerPhone ?? ''}
                                            maxLength={16}
                                            onChange={(e) => {
                                                const country = resolvePhoneCountry(undefined, selectedBusiness?.currency);
                                                setDraft({ ...draft, customerPhone: isLinked ? draft.customerPhone : formatPhone(e.target.value, country) });
                                            }}
                                            readOnly={isLinked}
                                            disabled={lockedAppt}
                                            title={isLinked ? t('appointments.drawer.form.phoneLocked') : undefined}
                                        />
                                        {!isLinked && !isPhoneComplete(draft.customerPhone ?? '', resolvePhoneCountry(undefined, selectedBusiness?.currency)) &&
                                         (draft.customerPhone ?? '') !== '' && (
                                            <span className="form-text text-danger small">{t('common.invalidPhone')}</span>
                                        )}
                                    </div>
                                    <div className="appt-edit-form__group">
                                        <label className="appt-edit-form__label">{t('appointments.drawer.form.email')}</label>
                                        <input
                                            type="email"
                                            value={draft.customerEmail ?? ''}
                                            onChange={(e) => setDraft({ ...draft, customerEmail: e.target.value })}
                                            readOnly={isLinked}
                                            disabled={lockedAppt}
                                            title={isLinked ? t('appointments.drawer.form.emailLocked') : undefined}
                                        />
                                    </div>
                                </div>
                                {isLinked && (
                                    <small className="appt-edit-form__hint">
                                        {t('appointments.drawer.form.linkedHint')}
                                    </small>
                                )}

                                <div className="appt-edit-form__group">
                                    <label className="appt-edit-form__label">
                                        {t('appointments.drawer.form.extraDiscount', { currency })}
                                    </label>
                                    <input
                                        type="number"
                                        min={0}
                                        step={0.01}
                                        placeholder="0"
                                        disabled={lockedAppt}
                                        value={
                                            draft.extraDiscount && draft.extraDiscount > 0
                                                ? draft.extraDiscount
                                                : ''
                                        }
                                        onChange={(e) =>
                                            setDraft({
                                                ...draft,
                                                extraDiscount: e.target.value === '' ? 0 : Number(e.target.value),
                                            })
                                        }
                                    />
                                </div>

                                <div className="appt-edit-form__group">
                                    <label className="appt-edit-form__label">{t('appointments.drawer.form.notes')}</label>
                                    <textarea
                                        rows={3}
                                        disabled={lockedAppt}
                                        value={draft.notes ?? ''}
                                        onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                                    />
                                </div>
                            </div>
                            );
                        })()}

                        {/* PAYMENT — transactions list (read-only here) */}
                        {activeTab === 'payment' && (() => {
                            const svc = services.find((s) => s.id === draft.serviceId);
                            const cli = clients.find((c) => String(c.id) === String(draft.clientId));
                            // VARIABLE services use the stored per-appointment price.
                            const base = (svc?.priceType === 'VARIABLE' ? draft.price : null)
                                ?? svc?.price ?? 0;
                            const pct = Math.max(0, Math.min(100, Number(cli?.discountPercent) || 0));
                            const clientDiscount = +(base * pct / 100).toFixed(2);
                            const extra = draft.extraDiscount ?? 0;
                            const collected = drawerTxns.reduce((s, t) => {
                                if (t.status === 'SUCCEEDED' || t.status === 'PARTIALLY_REFUNDED') {
                                    return s + (t.amount - (t.refundedAmount || 0));
                                }
                                return s;
                            }, 0);
                            const totalAfterDiscounts = +(base - clientDiscount - extra).toFixed(2);
                            const remaining = Math.max(0, +(totalAfterDiscounts - collected).toFixed(2));
                            return (
                            <div className="appt-payment-list">
                                {/* Breakdown summary — same shape as the Calendar Payment tab. */}
                                <div className="gs-price-breakdown" style={{ marginBottom: '0.75rem' }}>
                                    <div className="gs-price-breakdown__row">
                                        <span>{t('appointments.drawer.payment.servicePrice')}</span>
                                        <span>{currency}{base.toFixed(2)}</span>
                                    </div>
                                    {clientDiscount > 0 && (
                                        <div className="gs-price-breakdown__row gs-price-breakdown__row--discount">
                                            <span>
                                                {t('appointments.drawer.payment.clientDiscount')}
                                                <small> ({pct}% — {cli?.firstName}{cli?.lastName ? ` ${cli.lastName}` : ''})</small>
                                            </span>
                                            <span>−{currency}{clientDiscount.toFixed(2)}</span>
                                        </div>
                                    )}
                                    {extra > 0 && (
                                        <div className="gs-price-breakdown__row gs-price-breakdown__row--discount">
                                            <span>{t('appointments.drawer.payment.extraDiscount')}</span>
                                            <span>−{currency}{extra.toFixed(2)}</span>
                                        </div>
                                    )}
                                    {collected > 0 && (
                                        <div className="gs-price-breakdown__row gs-price-breakdown__row--discount">
                                            <span>{t('appointments.drawer.payment.alreadyPaid')}</span>
                                            <span>−{currency}{collected.toFixed(2)}</span>
                                        </div>
                                    )}
                                    <div className="gs-price-breakdown__row gs-price-breakdown__row--total">
                                        <span>{t('appointments.drawer.payment.totalDue')}</span>
                                        <span>{currency}{remaining.toFixed(2)}</span>
                                    </div>
                                </div>

                                <div className="appt-payment-list__cta">
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-sm"
                                        onClick={() => goRecordPayment(draft.id)}
                                    >
                                        {t('appointments.drawer.payment.recordInCalendar')}
                                    </button>
                                </div>
                                {drawerTxns.length === 0 ? (
                                    <div className="empty-hint" style={{ padding: '1rem' }}>
                                        {t('appointments.drawer.payment.noPayments')}
                                    </div>
                                ) : (
                                    <ul className="appt-payment-list__items">
                                        {drawerTxns.map((txn) => (
                                            <li key={txn.id}>
                                                <div className="appt-payment-list__head">
                                                    <strong>{t(`payments.method.${txn.method}`)}</strong>{txn.giftCardCode ? <small className="text-muted"> 🎁 {txn.giftCardCode}</small> : null}
                                                    <span className={`payments-status payments-status--${txn.status.toLowerCase()}`}>
                                                        {t(`payments.status.${txn.status}`, { defaultValue: TRANSACTION_STATUS_LABEL[txn.status] })}
                                                    </span>
                                                </div>
                                                <div className="appt-payment-list__body">
                                                    <span>{currency}{txn.amount.toFixed(2)}</span>
                                                    {txn.refundedAmount > 0 && (
                                                        <em>{t('appointments.drawer.payment.refunded', { currency, amount: txn.refundedAmount.toFixed(2) })}</em>
                                                    )}
                                                    <span className="appt-payment-list__time">
                                                        {new Date(txn.createdOn).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                                                    </span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            );
                        })()}

                        {/* HISTORY — timeline */}
                        {activeTab === 'history' && (
                            <div className="appt-history">
                                {drawerHistory.length === 0 ? (
                                    <div className="empty-hint" style={{ padding: '1rem' }}>
                                        {t('appointments.drawer.history.noHistory')}
                                    </div>
                                ) : (
                                    <ul className="appt-history__items">
                                        {drawerHistory.map((h) => (
                                            <li key={h.id}>
                                                <div className="appt-history__event">{histEventLabel(h.eventType)}</div>
                                                {h.payload && <div className="appt-history__payload">{histPayloadLabel(h.eventType, h.payload)}</div>}
                                                <div className="appt-history__meta">
                                                    {new Date(h.changedOn).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                                                    {h.changedBy && <> · {h.changedBy}</>}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </Drawer>
        </div>
    );
};

export default AppointmentsComponent;
